import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import invitationService from "../../features/invitations/service/invitationService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/invitations/types/invitationTypes";

export const fetchInvitations = createAsyncThunk(
  "invitations/fetchAll",
  async (filters, { rejectWithValue }) => {
    try {
      return await invitationService.getInvitations(filters);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchInvitationById = createAsyncThunk(
  "invitations/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      return await invitationService.getInvitationById(id);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createInvitation = createAsyncThunk(
  "invitations/create",
  async (data, { rejectWithValue }) => {
    try {
      return await invitationService.createInvitation(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateInvitation = createAsyncThunk(
  "invitations/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await invitationService.updateInvitation(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteInvitation = createAsyncThunk(
  "invitations/delete",
  async (id, { rejectWithValue }) => {
    try {
      await invitationService.deleteInvitation(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const publishInvitation = createAsyncThunk(
  "invitations/publish",
  async (id, { rejectWithValue }) => {
    try {
      return await invitationService.publishInvitation(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const unpublishInvitation = createAsyncThunk(
  "invitations/unpublish",
  async (id, { rejectWithValue }) => {
    try {
      return await invitationService.unpublishInvitation(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateInvitationImage = createAsyncThunk(
  "invitations/updateImage",
  async ({ id, imageKey, url }, { rejectWithValue }) => {
    try {
      return await invitationService.updateImage(id, imageKey, url);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPLOAD_FAILED);
    }
  },
);

export const updateInvitationTemplate = createAsyncThunk(
  "invitations/updateTemplate",
  async ({ id, templateUpdates }, { rejectWithValue }) => {
    try {
      return await invitationService.updateTemplate(id, templateUpdates);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateInvitationQrCode = createAsyncThunk(
  "invitations/updateQrCode",
  async ({ id, qrCodeUrl }, { rejectWithValue }) => {
    try {
      return await invitationService.updateQrCode(id, qrCodeUrl);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  invitations: [],
  selectedInvitation: null,
  loading: false,
  error: null,
  success: null,
  filters: { status: "all", search: "" },
};

const upsertInvitationInList = (state, updated) => {
  const idx = state.invitations.findIndex((i) => i.id === updated.id);
  if (idx !== -1) state.invitations[idx] = updated;
  if (state.selectedInvitation?.id === updated.id)
    state.selectedInvitation = updated;
};

const invitationSlice = createSlice({
  name: "invitations",
  initialState,
  reducers: {
    clearErrorState: (state) => {
      state.error = null;
    },
    clearSuccessState: (state) => {
      state.success = null;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedInvitation: (state) => {
      state.selectedInvitation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvitations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.loading = false;
        state.invitations = action.payload;
      })
      .addCase(fetchInvitations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInvitationById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInvitationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedInvitation = action.payload;
      })
      .addCase(fetchInvitationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createInvitation.fulfilled, (state, action) => {
        state.invitations.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createInvitation.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateInvitation.fulfilled, (state, action) => {
        upsertInvitationInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateInvitation.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteInvitation.fulfilled, (state, action) => {
        state.invitations = state.invitations.filter(
          (i) => i.id !== action.payload,
        );
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteInvitation.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(publishInvitation.fulfilled, (state, action) => {
        upsertInvitationInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.PUBLISH_SUCCESS;
      })
      .addCase(unpublishInvitation.fulfilled, (state, action) => {
        upsertInvitationInList(state, action.payload);
      })

      .addCase(updateInvitationImage.fulfilled, (state, action) => {
        upsertInvitationInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.IMAGE_UPLOADED;
      })
      .addCase(updateInvitationImage.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateInvitationTemplate.fulfilled, (state, action) => {
        upsertInvitationInList(state, action.payload);
      })
      .addCase(updateInvitationQrCode.fulfilled, (state, action) => {
        upsertInvitationInList(state, action.payload);
      });
  },
});

export const {
  clearErrorState,
  clearSuccessState,
  updateFilters,
  clearSelectedInvitation,
} = invitationSlice.actions;
export default invitationSlice.reducer;
